


import {
  defineComponent,
  computed,
  onMounted,
  watch,
  onUnmounted,
  PropType,
} from '@vue/composition-api';

import RealtimeModePane
  from '@/components/ExternalReports/HokkaidoAirport/components/RealtimeView/RealtimeModePane/index.vue';
import PhotoModal from '@/components/lib/PhotoModalWithEdit/index.vue';
import MapMiscInfo from '@/components/ExternalReports/HokkaidoAirport/components/common/MapMiscInfo.vue';
import type { HokkaidoAirportMtxExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMtx';
import type { HokkaidoAirportReportPhotoExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import type { HokkaidoAirportCarExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';
import ManualDownloadLinks
  from '@/components/ExternalReports/HokkaidoAirport/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/HokkaidoAirport/composables/usePaneControl';
import { useExtremeMap } from '@/components/ExternalReports/HokkaidoAirport/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/HokkaidoAirport/composables/usePhotoModal';
import {
  CAR_LIST_MAX_HEIGHT_RATE,
  CAR_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import { requestPhotos, revokePhotosObjectURLs, fetchImageAsObjectURL } from '@/lib/imageHelper';
import { useRequestGuard } from '@/composables/useRequestGuard';
import useHokkaidoAirportCars from '@/components/ExternalReports/HokkaidoAirport/composables/useCars';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
  },
  setup(props) {
    const {
      paneState,
      onChangeShowCarIcons: onChangeShowCarIconsOrig,
      onChangePhotoTypeVisibility: onChangePhotoTypeVisibilityOrig,
    } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: CAR_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: CAR_LIST_MAX_HEIGHT_RATE,
      },
    });

    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      redrawCarLayer,
      showHokkaidoAirportCarPopup,
      showHokkaidoAirportReportPhotoPopup,
      showHokkaidoAirportMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);

    const { doRequest } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const {
      carState,
      startCarUpdateInterval,
      stopCarUpdateInterval,
      selectCar,
      deselectCar,
      deselectSelectedCarReportPhotos,
      redrawCarLayerWatchValue,
    } = useHokkaidoAirportCars();

    watch(
      redrawCarLayerWatchValue,
      (value, oldValue) => {
        const fitToExtent = oldValue === 0 || oldValue === undefined;
        redrawHokkaidoAirportCarLayer({ fitToExtent });
      },
    );

    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
    } = usePhotoModal();
    const showPhotoModal = async(photoType: string, photos: HokkaidoAirportReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      showPhotoModalOrig(photoType, photos);
    };

    const movingCarsCount = computed<number>(() => {
      return carState.cars.filter(car => car.isMoving).length;
    });
    const stoppedCarsCount = computed<number>(() => {
      return carState.cars.filter(car => !car.isMoving).length;
    });

    interface RedrawHokkaidoAirportCarLayerOptions {
      fitToExtent?: boolean;
      setSelectedCarToCenter?: boolean;
    }
    const redrawHokkaidoAirportCarLayer = (options: RedrawHokkaidoAirportCarLayerOptions = {}) => {
      redrawCarLayer(options);
    };

    const onChangeShowCarIcons = () => {
      onChangeShowCarIconsOrig();
      redrawHokkaidoAirportCarLayer();
    };
    const onChangePhotoTypeVisibility = (photoType: string, isVisible: boolean) => {
      onChangePhotoTypeVisibilityOrig(photoType, isVisible);
      redrawHokkaidoAirportCarLayer();
    };

    const onClickCar = async(selectedCar: HokkaidoAirportCarExt) => {
      hidePopup();
      // 選択中の車両をクリックした場合、選択を解除する
      if (deselectCar(selectedCar.id)) {
        redrawHokkaidoAirportCarLayer();
        return;
      }
      await doRequest(async() => {
        await selectCar(selectedCar.id);
      }, '車両の取得に失敗しました。');
      const updated = carState.cars.find(c => c.id === selectedCar.id);
      showHokkaidoAirportCarPopup(updated ?? selectedCar);
      redrawHokkaidoAirportCarLayer({ setSelectedCarToCenter: true });
    };

    const onClickPhotoPin = async(photo: HokkaidoAirportReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage && photo.imagePath) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }
        showHokkaidoAirportReportPhotoPopup(photo);
      }
      redrawHokkaidoAirportCarLayer();
    };
    const onClickMtx = (mtx: HokkaidoAirportMtxExt) => {
      const isSelected = mtx.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showHokkaidoAirportMtxPopup(mtx);
      }
      redrawHokkaidoAirportCarLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedCarReportPhotos();
      redrawHokkaidoAirportCarLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    onMounted(async() => {
      await doRequest(async() => {
        await loadExtremeMapEssentials();
        await startCarUpdateInterval(props.userSettings);
      }, '車両の取得に失敗しました。');
    });

    onUnmounted(() => {
      stopCarUpdateInterval();
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      mapViewState,
      paneState,
      carState,
      photoModalState,
      // ref
      extremeMapRef,
      // methods
      onClickCar,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      onChangeShowCarIcons,
      onChangePhotoTypeVisibility,
      paneResizeStopped,
      showPhotoModal,
      hidePhotoModal,
      movingCarsCount,
      stoppedCarsCount,
    };
  },
  components: {
    RealtimeModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
